import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Badge } from '../../atoms/Badge/Badge';
import './SidebarNavItem.css';

interface SidebarNavItemProps {
  to: string;
  icon: ReactNode;
  label: string;
  end?: boolean;
  /**
   * An area with tabs is active on all of its tabs' pages, not only on `to`;
   * the sidebar decides and passes it here. Omitted: NavLink's own match.
   */
  active?: boolean;
  /** A number shown next to the label (e.g. notes waiting in To review); 0/undefined → none. */
  count?: number;
  /** Screen-reader text of the count, e.g. "2 waiting" (the badge itself is hidden from AT). */
  countLabel?: string;
}

const LINK = 'sidebar-nav-item__link';
const ACTIVE = 'sidebar-nav-item__link sidebar-nav-item__link--active nav-link--active';

function CountBadge({ count, countLabel }: { count?: number; countLabel?: string }) {
  if (!count || count <= 0) return null;
  return (
    <>
      <Badge tone="info" className="sidebar-nav-item__count" aria-hidden="true">
        {count > 99 ? '99+' : count}
      </Badge>
      {countLabel && <span className="sidebar-nav-item__sr-only">, {countLabel}</span>}
    </>
  );
}

export function SidebarNavItem({ to, icon, label, end = false, active, count, countLabel }: SidebarNavItemProps) {
  if (active !== undefined) {
    return (
      <li className="sidebar-nav-item">
        <Link to={to} className={active ? ACTIVE : LINK} aria-current={active ? 'page' : undefined}>
          <span className="sidebar-nav-item__icon" aria-hidden="true">{icon}</span>
          <span className="sidebar-nav-item__label">{label}</span>
          <CountBadge count={count} countLabel={countLabel} />
        </Link>
      </li>
    );
  }
  return (
    <li className="sidebar-nav-item">
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) => (isActive ? ACTIVE : LINK)}
      >
        <span className="sidebar-nav-item__icon" aria-hidden="true">{icon}</span>
        <span className="sidebar-nav-item__label">{label}</span>
        <CountBadge count={count} countLabel={countLabel} />
      </NavLink>
    </li>
  );
}

export default SidebarNavItem;
